/**
 * Community API functions
 * Uses publicAxios for unauthenticated requests
 * Authenticated requests require passing the axios instance from useAuthenticatedAxios
 */

import publicAxios from './publicAxios';

/**
 * Get all communities (public by default).
 *
 * Pass an authenticated axios instance as the second argument to opt into
 * admin-only response fields. `include=moderators` is the current expansion:
 * site admins get a `moderators` array on each community.
 *
 * Auth-gated includes (currently just 'moderators') require the
 * `axiosInstance` argument and will throw if it is missing. This is
 * defense-in-depth: today the server gate (`req.user?.role === 'admin'`)
 * already drops the include for anonymous callers, but throwing at the
 * call site (a) prevents a future maintainer from silently no-op'ing
 * (`getCommunities({include:'moderators'})` with no second arg used to
 * return an empty moderators field with no warning), and (b) keeps the
 * `include=moderators` param off the wire when there's no admin token,
 * so future gate relaxations can't accidentally leak the field.
 *
 * @param {object} filters - Optional filters { status, category, search, include }
 * @param {object} [axiosInstance] - Authenticated axios instance. Required
 *   when filters.include uses an auth-gated value (e.g. 'moderators').
 */
const AUTH_GATED_INCLUDES = new Set(['moderators']);

export const getCommunities = async (filters = {}, axiosInstance = null) => {
    // Normalize filters.include to an array of trimmed values so a caller
    // passing an array (or a comma-delimited string) is checked the same
    // way as a single value. `AUTH_GATED_INCLUDES.has(filters.include)`
    // does reference equality on Set, so without normalization an array
    // filters.include would slip past the guard.
    const includeValues = []
        .concat(filters.include || [])
        .flatMap((v) => String(v).split(','))
        .map((v) => v.trim())
        .filter(Boolean);
    const hasAuthGated = includeValues.some((v) => AUTH_GATED_INCLUDES.has(v));

    if (hasAuthGated && !axiosInstance) {
        throw new Error(
            `getCommunities: include uses an auth-gated value (one of ${[
                ...AUTH_GATED_INCLUDES,
            ].join(', ')}) — requires an authenticated axios instance.`
        );
    }

    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.category) params.append('category', filters.category);
    if (filters.search) params.append('search', filters.search);
    // Only attach include when we have an axios instance — keeps the param
    // off the wire for anonymous callers so a future gate relaxation can't
    // accidentally start returning the auth-gated field. Append each
    // include value separately so the server-side normalize-to-array path
    // sees them as repeated params, not a comma-joined string.
    if (includeValues.length > 0 && axiosInstance) {
        includeValues.forEach((v) => params.append('include', v));
    }

    const client = axiosInstance || publicAxios;
    const response = await client.get('/api/communities', {
        params,
    });
    return response.data;
};

/**
 * Get a single community by ID or slug (public)
 * @param {string} idOrSlug - Community ID or slug
 */
export const getCommunity = async (idOrSlug) => {
    const response = await publicAxios.get(
        `/api/communities/${encodeURIComponent(idOrSlug)}`
    );
    return response.data;
};

/**
 * Create a new community (authenticated)
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {object} communityData - Community data
 */
export const createCommunity = async (axiosInstance, communityData) => {
    const response = await axiosInstance.post(
        '/api/communities',
        communityData
    );
    return response.data;
};

/**
 * Update a community (authenticated)
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {string} id - Community ID
 * @param {object} updates - Fields to update
 */
export const updateCommunity = async (axiosInstance, id, updates) => {
    const response = await axiosInstance.put(`/api/communities/${id}`, updates);
    return response.data;
};

/**
 * Delete/archive a community (authenticated)
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {string} id - Community ID
 */
export const deleteCommunity = async (axiosInstance, id) => {
    const response = await axiosInstance.delete(`/api/communities/${id}`);
    return response.data;
};

/**
 * List communities awaiting admin approval (authenticated, site admin only).
 * @param {object} axiosInstance - Authenticated axios instance
 * @returns {Promise<Array>} - Array of pending communities with populated requestedBy user info
 */
export const getPendingCommunities = async (axiosInstance) => {
    const response = await axiosInstance.get('/api/communities/pending');
    return response.data;
};

/**
 * Lists needsModerators requests across communities (authenticated, site admin only).
 * @param {object} axiosInstance - Authenticated axios instance
 */
export const getNeedsModeratorsRequests = async (axiosInstance) => {
    const response = await axiosInstance.get(
        `/api/communities/needs-moderators/requests`
    );
    return response.data;
};

/**
 * Sets needsModerators (authenticated, site admin only).
 * Dismisses all pending applications on flip to false.
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {string} id - Community ID
 * @param {boolean} needsModerators - Value to set the flag to
 */
export const setNeedsModerators = async (
    axiosInstance,
    id,
    needsModerators
) => {
    const response = await axiosInstance.put(
        `/api/communities/${id}/needs-moderators`,
        { needsModerators }
    );
    return response.data;
};

/**
 * Approve or deny a moderator-raised needsModerators request (authenticated,
 * site admin only). Note the path is `/needs-moderators/request` (singular),
 * distinct from the `/needs-moderators` toggle above. Approving sets
 * needsModerators true; denying sets it false. Either way the server clears
 * needsModeratorsRequested.
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {string} id - Community ID
 * @param {'approve'|'deny'} decision - Whether to approve or deny the request
 */
export const decideNeedsModerators = async (axiosInstance, id, decision) => {
    const response = await axiosInstance.put(
        `/api/communities/${id}/needs-moderators/request`,
        { decision }
    );
    return response.data;
};

/**
 * Approve a pending community creation request (authenticated, site admin only).
 * Flips the community to active and creates a moderator membership for the requester.
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {string} id - Community ID
 */
export const approveCommunity = async (axiosInstance, id) => {
    const response = await axiosInstance.put(`/api/communities/${id}/approve`);
    return response.data;
};

/**
 * Reject a pending community creation request (authenticated, site admin only).
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {string} id - Community ID
 * @param {string} rejectionReason - Required, non-empty explanation for the requester
 */
export const rejectCommunity = async (axiosInstance, id, rejectionReason) => {
    const response = await axiosInstance.put(`/api/communities/${id}/reject`, {
        rejectionReason,
    });
    return response.data;
};

/**
 * Request that a community be flagged as needing moderators
 * (authenticated; intended for a moderator of that community).
 * @param {object} axiosInstance - Authenticated axios instance
 * @param {string} id - Community ID
 */
export const requestNeedsModerators = async (axiosInstance, id) => {
    const response = await axiosInstance.post(
        `/api/communities/${id}/needs-moderators/request`
    );
    return response.data;
};
